import type { User } from "@/lib/types";
import { storage, STORAGE_KEYS } from "@/utils/storage";
import {
  extractFriendInviteCodeFromPath,
  normalizeFriendInviteCode,
} from "@/lib/friend/inviteCode";
import { isOnboardingComplete } from "./onboarding";

export function buildFriendInvitePath(code: string): string | null {
  const normalized = normalizeFriendInviteCode(code);
  return normalized ? `/friend/room/${normalized}` : null;
}

export function buildFriendInviteUrl(code: string, origin?: string): string | null {
  const path = buildFriendInvitePath(code);
  if (!path) return null;
  const baseOrigin = origin ?? (typeof window !== "undefined" ? window.location.origin : "");
  return baseOrigin ? `${baseOrigin}${path}` : path;
}

/**
 * Internal destinations a sign-in may return to besides friend invites: the app's
 * game entry points and the public locale pages (a visitor who signed in from a
 * public game page lands back on it). Same-origin, path-only, no query/hash.
 */
const RETURNABLE_PATH = /^\/(play|leaderboard|events|auction|tic-tac-toe|friend|weekend-league|free-kicks|road-to-goal|trivia-mines|buscaminas|pistas|ultimo|minuto|ortak-futbolcu|son-harfle|daily-grid|football-tenable|horserace|codenames|dato-falso-futbolero|conexiones-futboleras|football-darts-501|squad-spin|guess-the-goal|missing-xi|daily\/challenges\/[a-z0-9-]+|(en|ka|es|tr|ru|pt|de)(\/[a-z0-9-]+(\/[a-z0-9-]+)?)?)\/?$/;

export function normalizePostAuthRedirect(pathname: string | null | undefined): string | null {
  if (!pathname) return null;
  const code = extractFriendInviteCodeFromPath(pathname);
  if (code) return buildFriendInvitePath(code);
  const path = pathname.split(/[?#]/)[0];
  if (!path.startsWith("/") || path.startsWith("//") || !RETURNABLE_PATH.test(path)) return null;
  return path.length > 1 ? path.replace(/\/$/, "") : path;
}

export function rememberPostAuthRedirect(pathname: string | null | undefined): void {
  const redirect = normalizePostAuthRedirect(pathname);
  if (!redirect) return;
  storage.set(STORAGE_KEYS.POST_AUTH_REDIRECT, redirect);
}

export function consumePostAuthRedirect(): string | null {
  const redirect = normalizePostAuthRedirect(storage.get(STORAGE_KEYS.POST_AUTH_REDIRECT, ""));
  storage.remove(STORAGE_KEYS.POST_AUTH_REDIRECT);
  return redirect;
}

export function peekPostAuthRedirect(): string | null {
  return normalizePostAuthRedirect(storage.get(STORAGE_KEYS.POST_AUTH_REDIRECT, ""));
}

export function getPostAuthEntryRoute(user: User | null | undefined): "/play" | "/onboarding" | string {
  if (!isOnboardingComplete(user)) return "/onboarding";
  return consumePostAuthRedirect() ?? "/play";
}
